import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonGroup, IconButton, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LoadingButton from '@mui/lab/LoadingButton';

type SendSplitButtonProps = {
  onSendNow: () => void;
  onSchedule: () => void;
  disabled?: boolean;
  loading?: boolean;
};

// Split-knapp för utskick: huvudknappen = "Skicka nu", dropdown-pil öppnar meny med "Schemalägg".
export const SendSplitButton = (props: SendSplitButtonProps) => {
  const { onSendNow, onSchedule, disabled = false, loading = false } = props;
  const { t } = useTranslation(['groupMessage']);

  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggleMenu = () => setMenuOpen((prev) => !prev);
  const handleCloseMenu = () => setMenuOpen(false);

  const handleSendNow = () => {
    handleCloseMenu();
    onSendNow();
  };

  const handleSchedule = () => {
    handleCloseMenu();
    onSchedule();
  };

  return (
    <>
      <ButtonGroup variant='contained' disableElevation ref={anchorRef} aria-label={t('groupMessage:create.send') as string}>
        <LoadingButton
          loading={loading}
          variant='contained'
          disableElevation
          startIcon={<SendIcon aria-hidden />}
          onClick={onSendNow}
          disabled={disabled}
        >
          {t('groupMessage:create.send')}
        </LoadingButton>
        <IconButton
          size='small'
          aria-controls={menuOpen ? 'send-split-menu' : undefined}
          aria-expanded={menuOpen ? 'true' : undefined}
          aria-haspopup='menu'
          aria-label={t('groupMessage:create.sendOptions') as string}
          onClick={handleToggleMenu}
          disabled={disabled || loading}
          sx={(theme) => ({
            color: 'primary.contrastText',
            bgcolor: 'primary.main',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderTopRightRadius: theme.shape.borderRadius,
            borderBottomRightRadius: theme.shape.borderRadius,
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': {
              color: 'action.disabled',
              bgcolor: 'action.disabledBackground',
            },
          })}
        >
          <ArrowDropDownIcon />
        </IconButton>
      </ButtonGroup>
      <Menu
        id='send-split-menu'
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleSendNow}>
          <SendIcon fontSize='small' sx={{ mr: 1 }} aria-hidden />
          {t('groupMessage:create.sendNow')}
        </MenuItem>
        <MenuItem onClick={handleSchedule}>
          <ScheduleIcon fontSize='small' sx={{ mr: 1 }} aria-hidden />
          {t('groupMessage:create.schedule')}
        </MenuItem>
      </Menu>
    </>
  );
};
